import clsx from "clsx"
import { useEffect, useState } from "react"

import useIsAnyDialogOpen from "@hooks/useIsAnyDialogOpen"
import useUser from "@hooks/useUser"
import useUserNavigationUtils from "@hooks/useUserNavigationUtils"

import extractURLSearchParam from "@utils/extractURLSearchParam"
import { faIcon } from "@utils/faIcon"
import { ifSpaceOrEnter } from "@utils/keyboard"

import { UserAvatar } from "@molecules"

import { Menu } from "@organisms"

import { SETTINGS_ROUTES } from "@routes/settings"

import { PROFILE_BOTTOM_SHEET_OPEN } from "@constants/searchParams"

import styles from "./NavSidebarProfileButton.module.scss"
import { NavSidebarProfileButtonProps } from "./NavSidebarProfileButton.types"

const statusFromUrl = extractURLSearchParam(PROFILE_BOTTOM_SHEET_OPEN)

export default function NavSidebarProfileButton(props: NavSidebarProfileButtonProps) {
    const {
        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isDisabled = false,
        defaultOpen = false,
    } = props

    const { user } = useUser()
    const [isOpen, setIsOpen] = useState<boolean>(statusFromUrl === "open" || defaultOpen)

    const { setIsAnyDialogOpen } = useIsAnyDialogOpen()
    const { logout, openHelpCenter, openChangelog, openRequestFeature, openContactSupport, goToSettings } =
        useUserNavigationUtils()

    // Registers only while open: mounting closed must not decrement another popover's count.
    useEffect(() => {
        if (isOpen) {
            setIsAnyDialogOpen(true)
            return () => setIsAnyDialogOpen(false)
        }
    }, [isOpen, setIsAnyDialogOpen])

    return (
        <Menu
            className={styles.base}
            title={user?.full_name || "Profile"}
            supportText={user?.email}
            headerLeft={user ? <UserAvatar user={user} size={"xl"} /> : undefined}
            isOpen={isOpen}
            popoverAlign="start"
            popoverSide="bottom"
            setIsOpen={setIsOpen}
            trigger={
                <div
                    role="button"
                    aria-disabled={isDisabled}
                    className={clsx(styles.button, {
                        [styles.isFocused]: _isFocused,
                        [styles.isHovered]: _isHovered,
                        [styles.isPressed]: isPressed || isOpen,
                        [styles.isDisabled]: isDisabled,
                    })}
                    tabIndex={isDisabled ? -1 : 0}
                    onClick={() => setIsOpen(!isOpen)}
                    onKeyDown={(e) => ifSpaceOrEnter(e, () => setIsOpen(!isOpen))}
                >
                    {user && <UserAvatar user={user} size={"md"} />}
                </div>
            }
            items={[
                {
                    type: "group",
                    items: [
                        {
                            text: user?.full_name || "",
                            supportText: user?.email,
                            hideOnMobile: true,
                            type: "labelItem",
                        },
                        {
                            icon: faIcon({ name: "gear", style: "regular" }),
                            title: "Settings",
                            path: SETTINGS_ROUTES.main,
                            onClick: goToSettings,
                        },
                    ],
                },
                { type: "divider" },
                {
                    type: "group",
                    items: [
                        {
                            icon: faIcon({ name: "circle-question", style: "regular" }),
                            title: "Help center",
                            onClick: openHelpCenter,
                        },
                        {
                            icon: faIcon({ name: "headset", style: "regular" }),
                            title: "Contact Support",
                            onClick: openContactSupport,
                        },
                        {
                            icon: faIcon({ name: "circle-info", style: "regular" }),
                            title: "Request Feature",
                            onClick: openRequestFeature,
                        },
                        {
                            icon: faIcon({ name: "bullhorn", style: "regular" }),
                            title: "What's new",
                            onClick: openChangelog,
                        },
                    ],
                },
                { type: "divider" },
                {
                    type: "group",
                    items: [
                        {
                            icon: faIcon({ name: "arrow-right-from-bracket", style: "regular" }),
                            title: "Log Out",
                            variant: "danger",
                            onClick: () => void logout(),
                        },
                    ],
                },
            ]}
        />
    )
}
